import {
  IRTIS_THEORETICAL_ERROR_REFRESH_INTERVAL,
  IRTIS_THEORETICAL_REFRESH_INTERVAL,
  ISO_DATE_FORMAT,
} from '../constants'
import { useParams } from 'react-router-dom'
import { sortPassages } from '../helpers/helpers'
import { isTheoreticalError } from '../helpers/debugOptions'
import { addDays, format } from 'date-fns'
import { mapTheoreticalPassages } from '../data/mappers'
import { getTheoreticalVertrekken } from '../data/travelinfoTrip.api'
import useSWR from 'swr'
import { useMemo } from 'react'

export const THEORETICAL_PREFIX = 'theoretical-'

export async function fetchTheoreticalPassages(key) {
  if (isTheoreticalError()) {
    console.log('Fake theoretical error')
    throw new Error('RTKN debug error for theoretical calls')
  }

  const stops = key.split(THEORETICAL_PREFIX)[1].split('+')

  const today = new Date()
  const tomorrow = addDays(new Date(), 1)

  const todaysPassages = await fetchTheoreticalPassagesForADate(stops, today)
  const nextDayPassages = await fetchTheoreticalPassagesForADate(
    stops,
    tomorrow,
  )

  const mappedTheoreticalPassages = mapTheoreticalPassages([
    ...todaysPassages,
    ...nextDayPassages,
  ])
  return sortPassages(mappedTheoreticalPassages)
}

export async function fetchTheoreticalPassagesForADate(stops, date) {
  const isoFormattedDate = format(date, ISO_DATE_FORMAT)
  return await getTheoreticalVertrekken({
    haltenummers: stops,
    exploitationDate: isoFormattedDate,
  })
}

function getLocalStorageFallbackData(stopNumbers, initialValue) {
  try {
    const key = THEORETICAL_PREFIX + stopNumbers
    // Get from local storage by key

    const item = window.localStorage.getItem(key)

    if (typeof item === 'undefined' || item === 'undefined' || item === null) {
      return initialValue
    }
    // remove all other keys, to remove other unused passages
    Object.keys(window.localStorage)
      .filter(
        (localStorageKey) =>
          localStorageKey.startsWith(THEORETICAL_PREFIX) &&
          localStorageKey !== key,
      )
      .forEach((localStorageKey) => {
        localStorage.removeItem(localStorageKey)
      })

    // Parse stored json or if none return initialValue
    return item ? JSON.parse(item) : initialValue
  } catch (error) {
    // If error also return initialValue
    // eslint-disable-next-line no-console
    console.log(error)
    return initialValue
  }
}

function saveLocalStorageFallbackData(stopNumbers, valueToStore) {
  try {
    const key = THEORETICAL_PREFIX + stopNumbers
    if (typeof valueToStore === 'undefined') {
      return
    }

    window.localStorage.setItem(key, JSON.stringify(valueToStore))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
  }
}

export function useTheoreticalPassages() {
  const { haltenummers } = useParams()

  const fallbackData = useMemo(() => {
    return getLocalStorageFallbackData(haltenummers, [])
  }, [haltenummers])

  const { data, error, isLoading } = useSWR(
    THEORETICAL_PREFIX + haltenummers,
    fetchTheoreticalPassages,
    {
      refreshInterval: IRTIS_THEORETICAL_REFRESH_INTERVAL,
      fallbackData,
      revalidateOnFocus: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        const errorTimeoutId = setTimeout(() => {
          revalidate({ retryCount })
          clearTimeout(errorTimeoutId)
        }, IRTIS_THEORETICAL_ERROR_REFRESH_INTERVAL)
      },
      onSuccess: (data) => {
        saveLocalStorageFallbackData(haltenummers, data)
      },
    },
  )

  return {
    theoreticalPassages: data,
    theoreticalPassagesError: error,
    theoreticalPassagesLoading: isLoading,
  }
}
